import React, { useState } from 'react';
import { ShoppingCart, Heart, Eye } from 'lucide-react';
import { Product } from '../types';
import { useCart } from '../contexts/CartContext';
import { placeholders } from '../lib/placeholders';
import { navigate } from '../lib/router';

interface ProductCardProps {
  product: Product;
}

export const ProductCard: React.FC<ProductCardProps> = ({ product }) => {
  const { addToCart } = useCart();
  const [isHovered, setIsHovered] = useState(false);

  const comparePrice = product.compare_at_price ?? product.originalPrice ?? null;
  const discountPercent = comparePrice
    ? Math.round(((comparePrice - product.price) / comparePrice) * 100)
    : 0;

  const primaryImage = product.image_url || product.images?.[0] || placeholders.product;
  const secondaryImage = product.images && product.images.length > 1 ? product.images[1] : null;
  const image = isHovered && secondaryImage ? secondaryImage : primaryImage;
  const productLink = `/product/${product.slug || product.id}`;

  return (
    <div
      className="card-hover relative overflow-hidden rounded-[26px] border border-primary/10 bg-white/70"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {discountPercent > 0 && (
        <>
          <div className="absolute left-4 top-4 z-10 rounded-full bg-primary px-3 py-1.5 text-xs font-semibold tracking-[0.14em] text-white shadow-lg">
            SALE
          </div>
          <div className="absolute right-4 top-4 z-10 rounded-full bg-primary px-3 py-1.5 text-xs font-semibold tracking-[0.14em] text-white shadow-lg">
            {discountPercent}% OFF
          </div>
        </>
      )}

      {product.is_bestseller && !discountPercent && (
        <div className="absolute right-4 top-4 z-10 rounded-full bg-accent px-3 py-1 text-xs font-semibold tracking-[0.14em] text-text shadow-lg">
          BESTSELLER
        </div>
      )}

      <div className="relative overflow-hidden group aspect-square bg-white">
        <a href={productLink} onClick={(e) => { e.preventDefault(); navigate(productLink); }}>
          <img
            src={image}
            alt={product.name}
            className="w-full h-full object-contain transition-transform duration-500 group-hover:scale-105"
          />
        </a>

        <div
          className={`absolute inset-0 flex items-center justify-center gap-3 bg-black/18 transition-opacity duration-300 ${
            isHovered ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <button
            onClick={() => addToCart(product)}
            className="rounded-full bg-background p-3 text-primary shadow-lg transition-all hover:scale-105 hover:bg-primary hover:text-white"
          >
            <ShoppingCart size={20} />
          </button>
          <button className="rounded-full bg-background p-3 text-primary shadow-lg transition-all hover:scale-105 hover:bg-primary hover:text-white">
            <Heart size={20} />
          </button>
          <a
            href={productLink}
            className="rounded-full bg-background p-3 text-primary shadow-lg transition-all hover:scale-105 hover:bg-primary hover:text-white"
            onClick={(e) => { e.preventDefault(); navigate(productLink); }}
          >
            <Eye size={20} />
          </a>
        </div>
      </div>

      <div className="p-5">
        {product.deity && (
          <p className="mb-2 text-xs font-semibold uppercase tracking-[0.16em] text-primary/80">{product.deity}</p>
        )}
        <a href={productLink} onClick={(e) => { e.preventDefault(); navigate(productLink); }}>
          <h3 className="mb-2 h-12 line-clamp-2 text-lg font-semibold text-text transition-colors hover:text-primary">
            {product.name}
          </h3>
        </a>
        {product.short_description && (
          <p className="mb-4 line-clamp-2 text-sm leading-6 text-charcoal/75">{product.short_description}</p>
        )}

        <div className="flex items-center gap-2 mb-3">
          <span className="text-xl font-bold text-text">₹{product.price}</span>
          {comparePrice && (
            <span className="text-sm text-charcoal/45 line-through">
              ₹{comparePrice}
            </span>
          )}
        </div>

        <button
          onClick={() => addToCart(product)}
          className="btn-primary w-full"
        >
          Add to Cart
        </button>
      </div>
    </div>
  );
};
